<script setup>
import { ref, computed, onMounted, watch, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import PipelineHeader from './PipelineHeader.vue';
import { usePipelinePermissions } from '../composables/usePipelinePermissions';
import PipelineCardsAPI from 'dashboard/api/pipelineCards';
import Modal from '../../../../components/Modal.vue';
import PipelineCardForm from './PipelineCardForm.vue';
import PipelineCardDetails from './PipelineCardDetails.vue';
import AgentAPI from '../../../../api/agents';
import CalendarMonth from '../../../../components/ui/DatePicker/components/CalendarMonth.vue';
import CalendarYear from '../../../../components/ui/DatePicker/components/CalendarYear.vue';
import CalendarWeekLabel from '../../../../components/ui/DatePicker/components/CalendarWeekLabel.vue';
import { CALENDAR_PERIODS } from '../../../../components/ui/DatePicker/helpers/DatePickerHelper';
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const props = defineProps({
  currentView: {
    type: String,
    default: 'agenda',
  },
  columns: {
    type: Array,
    default: () => [],
  },
  pipelineCards: {
    type: Array,
    default: () => [],
  },
});
const emit = defineEmits(['switchView']);
const { t, locale } = useI18n();

// Helper to convert locale format from pt_BR to pt-BR for Intl.DateTimeFormat
const getUserLocale = () => {
  return (locale.value || navigator.language || 'en-US').replace('_', '-');
};
const store = useStore();
const agendaSelectedPipeline = computed(
  () => store.getters['pipelines/getSelectedPipeline']
);
const { canViewPipeline } = usePipelinePermissions(agendaSelectedPipeline);
const items = ref([]);
const agents = ref({});
const searchQuery = ref('');
const activeFilters = ref(null);
const filteredResults = ref({ total: 0, stages: {} });
const currentMonth = ref(new Date());
const selectedDate = ref(new Date());
const showAddModal = ref(false);
const viewMode = ref('month'); // month, week, day

// Novas refs para o controle do mini calendário
const miniCalendarView = ref('days'); // 'days', 'months', 'years'
const { MONTH: PERIOD_MONTH, YEAR: PERIOD_YEAR } = CALENDAR_PERIODS;

// Refs para controle do modal de detalhes
const showItemDetails = ref(false);
const selectedItem = ref(null);

// Adicionar ref para controlar o estado de drag & drop
const dragOverCell = ref(null);

// Adicionar ref para controlar o estado de expansão
const isRecentItemsExpanded = ref(true);

// Adicionar nova ref para controlar a data selecionada no mini calendário
const selectedMiniDate = ref(new Date());

const showEditModal = ref(false);
const selectedItemToEdit = ref(null);

// Refs para modal de seleção de hora
const showTimePickerModal = ref(false);
const pendingSchedule = ref(null); // { itemId, date, isReschedule }
const selectedTime = ref('09:00');

// Refs para busca em itens recentes
const recentItemsSearchQuery = ref('');
const showRecentSearch = ref(false);

// Refs para modal de agendamento por clique no dia
const showScheduleModal = ref(false);
const scheduleModalDate = ref(null);
const scheduleModalSearchQuery = ref('');
const selectedItemToSchedule = ref(null);

// Helper para formatar data local sem conversão UTC
const toLocalDateString = date => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

// Atualizar a função getEventsForDate para usar o horário local
const getEventsForDate = date => {
  return items.value.filter(item => {
    const itemDate =
      item.item_details?.scheduled_at || item.item_details?.deadline_at;
    if (!itemDate) return false;

    const [datePart] = itemDate.split('T');
    const compareDate = toLocalDateString(date);

    return datePart === compareDate;
  });
};

// Computed para dias do mês atual
const daysInMonth = computed(() => {
  const year = currentMonth.value.getFullYear();
  const month = currentMonth.value.getMonth();
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const days = [];

  // Adicionar dias do mês anterior para completar a primeira semana
  const firstDayOfWeek = firstDay.getDay();
  for (let i = firstDayOfWeek - 1; i >= 0; i -= 1) {
    const date = new Date(year, month, -i);
    days.push({
      date,
      isCurrentMonth: false,
      events: getEventsForDate(date),
    });
  }

  // Adicionar dias do mês atual
  for (let date = 1; date <= lastDay.getDate(); date += 1) {
    const currentDate = new Date(year, month, date);
    days.push({
      date: currentDate,
      isCurrentMonth: true,
      events: getEventsForDate(currentDate),
    });
  }

  // Adicionar dias do próximo mês para completar a última semana
  const remainingDays = 42 - days.length; // 6 semanas * 7 dias = 42
  for (let i = 1; i <= remainingDays; i += 1) {
    const date = new Date(year, month + 1, i);
    days.push({
      date,
      isCurrentMonth: false,
      events: getEventsForDate(date),
    });
  }

  return days;
});

// Função para obter os dias da semana atual
const getWeekDays = () => {
  const today = selectedDate.value || new Date();
  const start = new Date(today);
  start.setDate(start.getDate() - start.getDay()); // Começa no domingo

  const days = [];
  for (let i = 0; i < 7; i += 1) {
    const date = new Date(start);
    date.setDate(start.getDate() + i);
    days.push({
      date,
      isCurrentMonth: date.getMonth() === currentMonth.value.getMonth(),
      events: getEventsForDate(date),
    });
  }
  return days;
};

// Adicionar computed para dias baseado no modo de visualização
const visibleDays = computed(() => {
  if (viewMode.value === 'week') {
    return getWeekDays();
  }
  return daysInMonth.value;
});

// Atualizar o computed weeks para usar visibleDays
const weeks = computed(() => {
  const days =
    viewMode.value === 'week' ? visibleDays.value : daysInMonth.value;
  const result = [];
  for (let i = 0; i < days.length; i += 7) {
    result.push(days.slice(i, i + 7));
  }
  return result;
});

// Computed para nomes dos dias da semana
const weekDays = computed(() => {
  return [
    t('PIPELINE.AGENDA.WEEK_DAYS.SUN'),
    t('PIPELINE.AGENDA.WEEK_DAYS.MON'),
    t('PIPELINE.AGENDA.WEEK_DAYS.TUE'),
    t('PIPELINE.AGENDA.WEEK_DAYS.WED'),
    t('PIPELINE.AGENDA.WEEK_DAYS.THU'),
    t('PIPELINE.AGENDA.WEEK_DAYS.FRI'),
    t('PIPELINE.AGENDA.WEEK_DAYS.SAT'),
  ];
});

// Adicionar função para navegar entre semanas
const changeWeek = direction => {
  const newDate = new Date(selectedDate.value);
  newDate.setDate(newDate.getDate() + direction * 7);
  selectedDate.value = newDate;
  currentMonth.value = new Date(newDate); // Atualiza o mês se necessário
};

// Atualizar a função de mudança de mês para considerar o modo
const changeMonth = direction => {
  if (viewMode.value === 'week') {
    changeWeek(direction * 4); // Aproximadamente um mês
    return;
  }

  const newMonth = new Date(currentMonth.value);
  newMonth.setMonth(newMonth.getMonth() + direction);
  currentMonth.value = newMonth;
  selectedDate.value = newMonth;
};

// Format date using user's locale
const formatDate = (date, options = { month: 'long', year: 'numeric' }) => {
  return new Intl.DateTimeFormat(getUserLocale(), {
    ...options,
  }).format(date);
};

// Format short date (day/month/year)
const formatShortDate = dateString => {
  const date = new Date(dateString);
  return new Intl.DateTimeFormat(getUserLocale(), {
    dateStyle: 'short',
  }).format(date);
};

// Função para verificar se é hoje
const isToday = date => {
  const today = new Date();
  return (
    date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear()
  );
};

// Formatar hora do evento (se existir)
// Formatar hora do evento (se existir) no fuso local do usuário.
// Strings ISO com 'Z' ou offset são UTC e precisam ser convertidas para
// o horário local do browser antes de exibir — caso contrário usuários
// em BRT (UTC-3) veriam 3h a mais (ex: salvou 09:00 BRT, exibia 12:00).
const formatEventTime = dateStr => {
  if (!dateStr) return '';
  // Se é apenas data (YYYY-MM-DD), não mostrar hora
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return '';

  const date = new Date(dateStr);
  // new Date() parseia ISO com Z como UTC e converte para local automaticamente.
  // getHours/getMinutes retornam valores no fuso do browser.
  if (Number.isNaN(date.getTime())) return '';

  const hours = date.getHours();
  const minutes = date.getMinutes();

  // Se for meia-noite local, não mostrar
  if (hours === 0 && minutes === 0) return '';

  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
};

// Atualizar o computed recentItems
const recentItems = computed(() => {
  // Pegar os 10 itens mais recentes, independente de terem ou não agendamento
  return items.value
    .filter(item => {
      // Filtrar apenas itens que não têm prazo/agendamento
      const hasScheduling =
        item.item_details?.scheduled_at || item.item_details?.deadline_at;
      return !hasScheduling;
    })
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
    .slice(0, 10);
});

// Função para carregar agentes (chamada uma vez)
const loadAgents = async () => {
  if (Object.keys(agents.value).length === 0) {
    try {
      const { data } = await AgentAPI.get();
      data.forEach(agent => {
        agents.value[agent.id] = agent;
      });
    } catch (e) {
      // Error handled silently
    }
  }
};

// Função para processar os cards do pipeline (usando a prop)
const processCards = cardsList => {
  const selectedPipeline = store.getters['pipelines/getSelectedPipeline'];
  const stages = selectedPipeline?.stages || {};

  return cardsList.map(item => {
    const agentId = item.item_details?.agent_id;
    const agent = agentId ? agents.value[agentId] : null;
    const stage = stages[item.pipeline_stage];

    return {
      id: item.id,
      title: item.title || item.item_details?.title || '',
      description: item.description || item.item_details?.description || '',
      priority: item.item_details?.priority || 'none',
      pipeline_id: item.pipeline_id,
      pipeline_stage: item.pipeline_stage,
      position: item.position,
      item_details: {
        ...item.item_details,
        _agent: agent,
      },
      stageName: stage?.name || item.pipeline_stage || '',
      stageColor: stage?.color || '#64748B',
      created_at: item.created_at,
      custom_attributes: item.custom_attributes || {},
    };
  });
};

// Atualizar items quando pipelineCards mudar
const updateItemsFromProps = () => {
  items.value = processCards(props.pipelineCards);
};

// Função para forçar recarregamento via store (usada após edições)
const fetchPipelineCards = async () => {
  await store.dispatch('pipelineCards/fetchPipelineCardItems');
};

// Handler para abrir o modal de agendamento ao clicar em um dia
const handleDayClickForSchedule = date => {
  scheduleModalDate.value = date;
  scheduleModalSearchQuery.value = '';
  selectedItemToSchedule.value = null;
  showScheduleModal.value = true;
};

// Handlers
const handleViewChange = view => {
  emit('switchView', view);
};

const handleDateClick = (date, events) => {
  selectedDate.value = date;
  // Abrir modal de agendamento em vez do modal de adicionar
  handleDayClickForSchedule(date, events);
};

const handleFilter = filters => {
  activeFilters.value = filters;
};

const handleSearch = query => {
  searchQuery.value = query;
};

const handleItemCreated = async item => {
  if (!item) return;
  showAddModal.value = false;
  await fetchPipelineCards();
};

// Resolve o card COMPLETO (store) a partir do id. O evento do calendário é um
// snapshot reduzido (processCards remove won_at/lost_at/status/owner...), então
// para o detail abrir com dados corretos passamos o card cru.
const findRawCard = id =>
  store.getters['pipelineCards/getPipelineCardItems']?.find(c => c.id === id) ||
  props.pipelineCards.find(c => c.id === id);

// Função para abrir detalhes do item
const handleEventClick = event => {
  // Passa o card completo (não o snapshot do calendário) para o PipelineCardDetails
  // ter won_at/lost_at/status e refletir corretamente ganho/perdido.
  selectedItem.value = findRawCard(event.id) || event;
  showItemDetails.value = true;
};

// Após ganho/perdido/edição no detail: o modal aberto precisa refletir o novo
// estado na hora. O refetch da lista (fetchPipelineCardItems) tem cache de 5min
// e NÃO aceita force, então buscamos o card individual fresco via getCard e o
// commitamos no store — isso atualiza tanto o modal (selectedItem) quanto o
// calendário (props.pipelineCards vem do store). Sem isto, marcava Perdido/Ganho
// mas o modal seguia mostrando o estado anterior ("Aberto").
const handleDetailItemUpdated = async () => {
  const currentId = selectedItem.value?.id;
  if (!currentId) {
    await fetchPipelineCards();
    return;
  }
  try {
    const { data } = await PipelineCardsAPI.getCard(currentId);
    if (data) {
      selectedItem.value = data;
      store.commit('pipelineCards/UPDATE_PIPELINE_CARD_ITEM', data);
    }
  } catch (e) {
    // Fallback: tenta o estado do store/lista.
    await fetchPipelineCards();
    const updated = findRawCard(currentId);
    if (updated) selectedItem.value = updated;
  }
};

// Função para fechar detalhes do item
const handleCloseDetails = () => {
  showItemDetails.value = false;
  selectedItem.value = null;
};

// Função para abrir o modal de seleção de hora
const handleCardDrop = (itemId, date, isReschedule = false) => {
  pendingSchedule.value = { itemId, date, isReschedule };
  selectedTime.value = '09:00'; // Reset para hora padrão
  showTimePickerModal.value = true;
};

// Função para confirmar o agendamento com hora
const confirmScheduleWithTime = async () => {
  if (!pendingSchedule.value) return;

  try {
    const { itemId, date, isReschedule } = pendingSchedule.value;
    const item = items.value.find(i => i.id === itemId);
    if (!item) return;

    // Obter a data atual do item antes da remarcação
    const currentDate =
      item.item_details?.scheduling_type === 'deadline'
        ? item.item_details?.deadline_at
        : item.item_details?.scheduled_at;

    // Criar datetime combinando data e hora selecionadas
    // Usar UTC para evitar problemas de timezone
    const year = date.getFullYear();
    const month = date.getMonth();
    const day = date.getDate();
    const [hours, minutes] = selectedTime.value.split(':');
    const scheduledDate = new Date(
      year,
      month,
      day,
      parseInt(hours, 10),
      parseInt(minutes, 10),
      0,
      0
    );

    // Criar uma cópia do item_details para não modificar o original
    const updatedItemDetails = {
      ...item.item_details,
      scheduling_type: 'deadline',
      deadline_at: scheduledDate.toISOString(),
      rescheduled: isReschedule,
      rescheduling_history: isReschedule
        ? [
            ...(item.item_details.rescheduling_history || []),
            {
              from_date: currentDate,
              to_date: scheduledDate.toISOString(),
              changed_at: new Date().toISOString(),
            },
          ]
        : [],
    };

    const payload = {
      pipeline_id: item.pipeline_id,
      pipeline_stage: item.pipeline_stage,
      position: item.position,
      item_details: updatedItemDetails,
      custom_attributes: item.custom_attributes || {},
    };

    const response = await PipelineCardsAPI.updateCard(itemId, payload);

    // Atualizar o item localmente no store para refletir a mudança imediatamente
    if (response.data) {
      store.dispatch('pipelineCards/replaceCard', response.data);
    }
  } catch {
    // Error handled silently
  } finally {
    showTimePickerModal.value = false;
    pendingSchedule.value = null;
  }
};

// Função para cancelar o agendamento
const cancelSchedule = () => {
  showTimePickerModal.value = false;
  pendingSchedule.value = null;
};

// Atualizar as funções de drag & drop
const allowDrop = (e, date) => {
  e.preventDefault();
  dragOverCell.value = date;
};

const handleDragLeave = () => {
  dragOverCell.value = null;
};

// Função para iniciar drag de eventos já agendados no calendário
const handleEventDragStart = (e, event) => {
  e.dataTransfer.setData('itemId', event.id);
  e.dataTransfer.setData('isReschedule', 'true');
  e.dataTransfer.effectAllowed = 'move';
  // Adicionar classe visual durante o drag
  e.target.classList.add('opacity-50', 'scale-95');
};

// Função para limpar visual após drag terminar
const handleEventDragEnd = e => {
  e.target.classList.remove('opacity-50', 'scale-95');
};

const handleDrop = async (e, date) => {
  e.preventDefault();
  const itemId = parseInt(e.dataTransfer.getData('itemId'), 10);
  const isReschedule = e.dataTransfer.getData('isReschedule') === 'true';
  if (!itemId) return;

  dragOverCell.value = null;
  await handleCardDrop(itemId, date, isReschedule);
};

// Adicionar função para alternar expansão
const toggleRecentItems = () => {
  isRecentItemsExpanded.value = !isRecentItemsExpanded.value;
};

const handleRecentSearch = () => {
  showRecentSearch.value = !showRecentSearch.value;
  if (showRecentSearch.value) {
    nextTick(() => {
      document.getElementById('recent-items-search')?.focus();
    });
  } else {
    recentItemsSearchQuery.value = '';
  }
};

// Adicione esta função para lidar com a edição
const handleItemEdit = item => {
  selectedItemToEdit.value = {
    ...item,
    pipeline_stage:
      item.pipeline_stage ||
      store.getters['pipelines/getSelectedPipeline']?.default_stage,
  };
  showEditModal.value = true;
};

// Adicione esta função para lidar com o item atualizado
const handleItemUpdated = async () => {
  showEditModal.value = false;
  selectedItemToEdit.value = null;
  showItemDetails.value = false;
  await fetchPipelineCards();
};

// Lifecycle hooks
onMounted(async () => {
  await loadAgents();
  updateItemsFromProps();
});

// Watch para atualizar quando pipelineCards prop mudar
watch(
  () => props.pipelineCards,
  () => {
    updateItemsFromProps();
  },
  { deep: true, immediate: true }
);

// Watch para atualizar quando o funil mudar (reprocessar nomes dos estágios)
watch(
  () => store.getters['pipelines/getSelectedPipeline'],
  () => {
    updateItemsFromProps();
  }
);

// Adicionar watch para o viewMode
watch(viewMode, newMode => {
  if (newMode === 'week') {
    selectedDate.value = new Date(); // Reset para a semana atual
  }
});

// Computed para eventos do mês atual
const currentMonthEvents = computed(() => {
  return items.value.filter(item => {
    const itemDate =
      item.item_details?.scheduled_at || item.item_details?.deadline_at;
    if (!itemDate) return false;

    const date = new Date(itemDate);
    return (
      date.getMonth() === currentMonth.value.getMonth() &&
      date.getFullYear() === currentMonth.value.getFullYear()
    );
  });
});

// Computed para eventos remarcados
const rescheduledEvents = computed(() => {
  return items.value.filter(item => item.item_details?.rescheduled);
});

// Computed para eventos pendentes (sem data definida)
const pendingEvents = computed(() => {
  return items.value.filter(item => {
    return !item.item_details?.scheduled_at && !item.item_details?.deadline_at;
  });
});

// Função auxiliar para comparar se duas datas são o mesmo dia
const isSameDay = (date1, date2) => {
  return (
    date1.getFullYear() === date2.getFullYear() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getDate() === date2.getDate()
  );
};

// Handlers para os novos componentes do mini calendário
const openMiniCalendarMonthView = () => {
  miniCalendarView.value = 'months';
};

const handleMiniCalendarSetView = (calendarType, period) => {
  if (period === PERIOD_YEAR) {
    miniCalendarView.value = 'years';
  } else if (period === PERIOD_MONTH) {
    miniCalendarView.value = 'months';
  } else {
    miniCalendarView.value = 'days';
  }
};

const handleMiniCalendarSelectMonth = monthIndex => {
  const newDate = new Date(currentMonth.value);
  newDate.setMonth(monthIndex);
  currentMonth.value = newDate;
  selectedMiniDate.value = new Date(newDate); // Sincronizar selectedMiniDate
  miniCalendarView.value = 'days';
};

const handleMiniCalendarSelectYear = year => {
  const newDate = new Date(currentMonth.value);
  newDate.setFullYear(year);
  currentMonth.value = newDate;
  selectedMiniDate.value = new Date(newDate); // Sincronizar selectedMiniDate
  miniCalendarView.value = 'months'; // Mudar para visualização de meses após selecionar o ano
};

const handleMiniCalendarPrev = () => {
  // Usado por CalendarMonth para navegar para o ano anterior
  const newDate = new Date(currentMonth.value);
  newDate.setFullYear(newDate.getFullYear() - 1);
  currentMonth.value = newDate;
  selectedMiniDate.value = new Date(newDate);
};

const handleMiniCalendarNext = () => {
  // Usado por CalendarMonth para navegar para o próximo ano
  const newDate = new Date(currentMonth.value);
  newDate.setFullYear(newDate.getFullYear() + 1);
  currentMonth.value = newDate;
  selectedMiniDate.value = new Date(newDate);
};

// Computed para itens recentes filtrados (com busca)
const filteredRecentItems = computed(() => {
  const query = recentItemsSearchQuery.value.toLowerCase().trim();
  if (!query) return recentItems.value;

  return recentItems.value.filter(item => {
    const title = (item.title || '').toLowerCase();
    const description = (item.description || '').toLowerCase();
    const stageName = (item.stageName || '').toLowerCase();
    return (
      title.includes(query) ||
      description.includes(query) ||
      stageName.includes(query)
    );
  });
});

// Computed para todos os itens sem data (para o modal de agendamento)
const allUnscheduledItems = computed(() => {
  return items.value
    .filter(item => {
      const hasScheduling =
        item.item_details?.scheduled_at || item.item_details?.deadline_at;
      return !hasScheduling;
    })
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
});

// Computed para itens filtrados no modal de agendamento
const filteredScheduleModalItems = computed(() => {
  const query = scheduleModalSearchQuery.value.toLowerCase().trim();
  if (!query) return allUnscheduledItems.value.slice(0, 20); // Mostrar os 20 mais recentes

  return allUnscheduledItems.value.filter(item => {
    const title = (item.title || '').toLowerCase();
    const description = (item.description || '').toLowerCase();
    const stageName = (item.stageName || '').toLowerCase();
    return (
      title.includes(query) ||
      description.includes(query) ||
      stageName.includes(query)
    );
  });
});

// Computed para verificar se um evento está atrasado
const isOverdue = item => {
  const deadline = item.item_details?.deadline_at;
  if (!deadline) return false;
  const deadlineDate = new Date(deadline);
  const now = new Date();
  return deadlineDate < now;
};

// Get color based on priority using CSS variables
const getPriorityColor = priority => {
  switch (priority) {
    case 'urgent':
      return 'var(--n-ruby-9)';
    case 'high':
      return 'var(--n-orange-9)';
    case 'medium':
      return 'var(--n-amber-9)';
    case 'low':
      return 'var(--n-green-9)';
    default:
      return 'var(--n-slate-9)';
  }
};

// Handler para selecionar um item no modal de agendamento
const selectItemForSchedule = item => {
  selectedItemToSchedule.value = item;
};

// Handler para confirmar o agendamento do modal
const confirmScheduleFromModal = () => {
  if (!selectedItemToSchedule.value || !scheduleModalDate.value) return;

  // Fechar o modal de seleção e abrir o de hora
  showScheduleModal.value = false;
  handleCardDrop(selectedItemToSchedule.value.id, scheduleModalDate.value);
};

// Handler para cancelar o modal de agendamento
const cancelScheduleModal = () => {
  showScheduleModal.value = false;
  scheduleModalDate.value = null;
  scheduleModalSearchQuery.value = '';
  selectedItemToSchedule.value = null;
};
</script>

<template>
  <div class="flex flex-col h-full bg-n-background">
    <!-- Access denied: agent is not a member of this pipeline -->
    <div
      v-if="!canViewPipeline"
      class="flex flex-1 items-center justify-center p-8"
    >
      <div class="text-center max-w-sm">
        <fluent-icon
          icon="lock-closed"
          size="40"
          class="mx-auto mb-4 text-n-slate-10"
        />
        <h3 class="text-heading-3 text-n-slate-12 mb-2">
          {{ t('PIPELINE.ACCESS_DENIED.TITLE') }}
        </h3>
        <p class="text-sm text-n-slate-11">
          {{ t('PIPELINE.ACCESS_DENIED.DESCRIPTION') }}
        </p>
      </div>
    </div>

    <template v-else>
      <PipelineHeader
        :current-view="currentView"
        :search-results="filteredResults"
        :active-filters="activeFilters"
        :columns="columns"
        :pipeline-cards="items"
        @switch-view="handleViewChange"
        @filter="handleFilter"
        @search="handleSearch"
      />

      <div class="flex flex-1 min-h-0 flex-col">
        <div class="flex flex-1 min-h-0">
          <!-- Mini Calendar Sidebar -->
          <div
            class="w-[260px] border-r border-n-weak flex flex-col overflow-hidden relative z-[3] h-full [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          >
            <div class="p-3 flex-shrink-0 flex flex-col h-full">
              <!-- Mini Calendar Header -->
              <div
                v-if="miniCalendarView === 'days'"
                class="flex items-center justify-between mb-3"
              >
                <span
                  class="text-xs font-semibold text-n-slate-12 cursor-pointer hover:text-n-blue-11 capitalize transition-colors"
                  @click="openMiniCalendarMonthView"
                >
                  {{ formatDate(currentMonth) }}
                </span>
                <div class="flex gap-0.5">
                  <button
                    type="button"
                    class="p-1 rounded-md hover:bg-n-alpha-2 text-n-slate-10 transition-colors"
                    @click="changeMonth(-1)"
                  >
                    <Icon icon="i-lucide-chevron-left" class="size-3" />
                  </button>
                  <button
                    type="button"
                    class="p-1 rounded-md hover:bg-n-alpha-2 text-n-slate-10 transition-colors"
                    @click="changeMonth(1)"
                  >
                    <Icon icon="i-lucide-chevron-right" class="size-3" />
                  </button>
                </div>
              </div>

              <!-- CalendarMonth View -->
              <CalendarMonth
                v-if="miniCalendarView === 'months'"
                calendar-type="start"
                :start-current-date="currentMonth"
                :end-current-date="currentMonth"
                class="mb-4"
                @select-month="handleMiniCalendarSelectMonth"
                @set-view="handleMiniCalendarSetView"
                @prev="handleMiniCalendarPrev"
                @next="handleMiniCalendarNext"
              />

              <!-- CalendarYear View -->
              <CalendarYear
                v-if="miniCalendarView === 'years'"
                calendar-type="start"
                :start-current-date="currentMonth"
                :end-current-date="currentMonth"
                class="mb-4"
                @select-year="handleMiniCalendarSelectYear"
                @prev="() => {}"
                @next="() => {}"
              />

              <!-- Mini Calendar Grid (apenas se a visualização for 'days') -->
              <div
                v-if="miniCalendarView === 'days'"
                class="mini-calendar mb-6 flex-shrink-0"
              >
                <!-- Mini Week Days - Substituído por CalendarWeekLabel -->
                <CalendarWeekLabel class="mb-2" />

                <!-- Mini Days Grid -->
                <div class="grid grid-cols-7 gap-1">
                  <div
                    v-for="day in daysInMonth"
                    :key="day.date"
                    class="relative aspect-square flex items-center justify-center text-xs rounded-full cursor-pointer transition-colors"
                    :class="[
                      // Background: today > current month > other month
                      isToday(day.date)
                        ? 'bg-n-blue-9'
                        : day.isCurrentMonth
                          ? 'bg-n-solid-2 hover:bg-n-alpha-2'
                          : 'bg-n-alpha-1',
                      // Text color: today > current month > other month
                      isToday(day.date)
                        ? 'text-white font-medium'
                        : day.isCurrentMonth
                          ? 'text-n-slate-12'
                          : 'text-n-slate-9',
                      // Selected ring (not for today)
                      isSameDay(day.date, selectedMiniDate) &&
                      !isToday(day.date)
                        ? 'ring-2 ring-n-blue-9'
                        : '',
                    ]"
                    @click="selectedMiniDate = day.date"
                  >
                    {{ day.date.getDate() }}
                    <!-- Event indicator dot -->
                    <div
                      v-if="day.events.length > 0"
                      class="absolute bottom-0.5 left-1/2 -translate-x-1/2 size-1 rounded-full"
                      :class="isToday(day.date) ? 'bg-white' : 'bg-n-blue-9'"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Main Calendar -->
          <div class="flex-1 flex flex-col min-h-0">
            <!-- Main Calendar Header -->
            <div class="flex-shrink-0 bg-n-solid-2 z-10">
              <div class="flex items-center justify-between px-5 py-2.5">
                <h2 class="text-sm font-semibold text-n-slate-12 capitalize">
                  {{
                    formatDate(
                      viewMode === 'week' ? selectedDate : currentMonth
                    )
                  }}
                </h2>
                <div class="flex items-center gap-2">
                  <button
                    type="button"
                    class="px-2.5 py-1 text-[11px] font-medium text-n-slate-11 hover:text-n-slate-12 hover:bg-n-alpha-1 rounded-md transition-colors"
                    @click="
                      selectedDate = new Date();
                      currentMonth = new Date();
                    "
                  >
                    {{ t('PIPELINE.AGENDA.VIEW_MODES.TODAY') }}
                  </button>
                  <nav class="flex items-center rounded-lg bg-n-alpha-1 p-0.5">
                    <button
                      type="button"
                      class="px-2.5 py-1 text-[11px] font-medium rounded-md transition-all duration-150"
                      :class="
                        viewMode === 'month'
                          ? 'bg-n-solid-2 shadow-sm text-n-slate-12'
                          : 'text-n-slate-10 hover:text-n-slate-12'
                      "
                      @click="viewMode = 'month'"
                    >
                      {{ t('PIPELINE.AGENDA.VIEW_MODES.MONTH') }}
                    </button>
                    <button
                      type="button"
                      class="px-2.5 py-1 text-[11px] font-medium rounded-md transition-all duration-150"
                      :class="
                        viewMode === 'week'
                          ? 'bg-n-solid-2 shadow-sm text-n-slate-12'
                          : 'text-n-slate-10 hover:text-n-slate-12'
                      "
                      @click="viewMode = 'week'"
                    >
                      {{ t('PIPELINE.AGENDA.VIEW_MODES.WEEK') }}
                    </button>
                  </nav>
                </div>
              </div>

              <!-- Week Days Header -->
              <div
                class="grid grid-cols-7 border-b border-t border-n-weak bg-n-alpha-1"
              >
                <div
                  v-for="day in weekDays"
                  :key="day"
                  class="py-2 text-[10px] font-semibold text-n-slate-10 text-center uppercase tracking-wider border-r last:border-r-0 border-n-weak"
                >
                  {{ day }}
                </div>
              </div>
            </div>

            <!-- Calendar Grid -->
            <div
              class="grid flex-1 min-h-0 border-l border-t border-n-weak relative isolate z-[1] overflow-y-auto"
              :class="{
                'grid-rows-[repeat(6,1fr)]': viewMode === 'month',
                'grid-rows-[1fr] h-full overflow-hidden border-none':
                  viewMode === 'week',
              }"
            >
              <div
                v-for="week in weeks"
                :key="week[0].date"
                class="grid grid-cols-7"
              >
                <div
                  v-for="day in week"
                  :key="day.date"
                  class="relative p-2 border-r border-b border-t border-n-weak last:border-r-0 flex flex-col isolate hover:z-[1] hover:bg-n-alpha-1 transition-all duration-200"
                  :class="{
                    'bg-n-alpha-1':
                      !day.isCurrentMonth &&
                      dragOverCell?.getTime?.() !== day.date.getTime(),
                    'bg-n-solid-2':
                      day.isCurrentMonth &&
                      dragOverCell?.getTime?.() !== day.date.getTime(),
                    today: isToday(day.date),
                    'h-full': viewMode === 'week',
                    'flex flex-col h-full': viewMode === 'week',
                    'bg-n-blue-3 ring-2 ring-n-blue-9 ring-inset':
                      dragOverCell?.getTime?.() === day.date.getTime(),
                  }"
                  @click="handleDateClick(day.date, day.events)"
                  @dragover="e => allowDrop(e, day.date)"
                  @dragleave="handleDragLeave"
                  @drop="e => handleDrop(e, day.date)"
                >
                  <div class="h-full flex flex-col">
                    <span
                      class="text-sm mb-1"
                      :class="{
                        'text-n-slate-9': !day.isCurrentMonth,
                        'text-n-slate-12': day.isCurrentMonth,
                        'bg-n-blue-9 text-white rounded-full w-6 h-6 flex items-center justify-center':
                          isToday(day.date),
                      }"
                    >
                      {{ day.date.getDate() }}
                    </span>

                    <!-- Time-slotted events for week view -->
                    <template v-if="viewMode === 'week'">
                      <div
                        class="mt-1 relative flex-1 overflow-y-auto overflow-x-hidden min-h-0 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden space-y-1"
                      >
                        <div
                          v-for="event in day.events"
                          :key="event.id"
                          class="text-xs p-1 rounded truncate cursor-move hover:opacity-90 mb-1 transition-all duration-200"
                          :class="{
                            'ring-2 ring-n-ruby-9 ring-opacity-50':
                              isOverdue(event),
                          }"
                          :style="{
                            backgroundColor: isOverdue(event)
                              ? 'var(--n-ruby-3)'
                              : event.stageColor + '20',
                            color: isOverdue(event)
                              ? 'var(--n-ruby-11)'
                              : event.stageColor,
                            borderLeft: `3px solid ${event.priority && event.priority !== 'none' ? getPriorityColor(event.priority) : event.stageColor}`,
                          }"
                          :title="`${event.title}${event.priority && event.priority !== 'none' ? ' - ' + event.priority : ''}${isOverdue(event) ? ' (' + t('PIPELINE.CARD.OVERDUE') + ')' : ''} - ${t('PIPELINE.AGENDA.DRAG_TO_RESCHEDULE')}`"
                          draggable="true"
                          @dragstart="e => handleEventDragStart(e, event)"
                          @dragend="handleEventDragEnd"
                          @click.stop="handleEventClick(event)"
                        >
                          <div class="flex items-center gap-1">
                            <Icon
                              v-if="isOverdue(event)"
                              icon="i-lucide-alert-triangle"
                              class="size-3 text-n-ruby-9 flex-shrink-0"
                            />
                            <Icon
                              v-else
                              :icon="
                                event.item_details?.scheduling_type ===
                                'deadline'
                                  ? 'i-lucide-alarm-clock'
                                  : 'i-lucide-book-open'
                              "
                              class="size-3"
                            />
                            <span
                              v-if="
                                formatEventTime(
                                  event.item_details?.deadline_at ||
                                    event.item_details?.scheduled_at
                                )
                              "
                              class="font-medium flex-shrink-0"
                            >
                              {{
                                formatEventTime(
                                  event.item_details?.deadline_at ||
                                    event.item_details?.scheduled_at
                                )
                              }}
                            </span>
                            <span class="truncate">{{ event.title }}</span>
                          </div>
                        </div>
                      </div>
                    </template>

                    <!-- Regular events for month view -->
                    <template v-else>
                      <!-- Events -->
                      <div
                        class="mt-1 relative flex-1 overflow-y-auto overflow-x-hidden min-h-0 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden space-y-1"
                      >
                        <div
                          v-for="event in day.events.slice(0, 3)"
                          :key="event.id"
                          class="text-xs p-1 rounded truncate cursor-move hover:opacity-90 transition-all duration-200"
                          :class="{
                            'ring-1 ring-n-ruby-9': isOverdue(event),
                          }"
                          :style="{
                            backgroundColor: isOverdue(event)
                              ? 'var(--n-ruby-3)'
                              : event.stageColor + '20',
                            color: isOverdue(event)
                              ? 'var(--n-ruby-11)'
                              : event.stageColor,
                            borderLeft: `3px solid ${event.priority && event.priority !== 'none' ? getPriorityColor(event.priority) : isOverdue(event) ? 'var(--n-ruby-9)' : event.stageColor}`,
                          }"
                          :title="`${event.title}${event.priority && event.priority !== 'none' ? ' - ' + event.priority : ''}${isOverdue(event) ? ' (' + t('PIPELINE.CARD.OVERDUE') + ')' : ''} - ${t('PIPELINE.AGENDA.DRAG_TO_RESCHEDULE')}`"
                          draggable="true"
                          @dragstart="e => handleEventDragStart(e, event)"
                          @dragend="handleEventDragEnd"
                          @click.stop="handleEventClick(event)"
                        >
                          <div class="flex items-center gap-1">
                            <Icon
                              v-if="isOverdue(event)"
                              icon="i-lucide-alert-triangle"
                              class="size-3 text-n-ruby-9 flex-shrink-0"
                            />
                            <Icon
                              v-else
                              :icon="
                                event.item_details?.scheduling_type ===
                                'deadline'
                                  ? 'i-lucide-alarm-clock'
                                  : 'i-lucide-book-open'
                              "
                              class="size-3 flex-shrink-0"
                            />
                            <span
                              v-if="
                                formatEventTime(
                                  event.item_details?.deadline_at ||
                                    event.item_details?.scheduled_at
                                )
                              "
                              class="font-medium flex-shrink-0"
                            >
                              {{
                                formatEventTime(
                                  event.item_details?.deadline_at ||
                                    event.item_details?.scheduled_at
                                )
                              }}
                            </span>
                            <span class="truncate">{{ event.title }}</span>
                          </div>
                        </div>
                        <div
                          v-if="day.events.length > 3"
                          class="text-xs text-n-slate-10 pl-1"
                        >
                          {{
                            t('PIPELINE.AGENDA.MORE_EVENTS', {
                              count: day.events.length - 3,
                            })
                          }}
                        </div>
                      </div>
                    </template>
                  </div>
                </div>
              </div>
            </div>

            <!-- Resumo do Mês -->
            <div
              class="flex-shrink-0 border-t border-n-weak bg-n-solid-2 relative z-[3]"
            >
              <div class="flex items-center gap-5 px-5 py-2">
                <!-- Total de Eventos -->
                <div class="flex items-center gap-1.5">
                  <Icon icon="i-lucide-layers" class="size-3 text-n-slate-10" />
                  <span class="text-[11px] text-n-slate-11">
                    {{ t('PIPELINE.AGENDA.TOTAL_EVENTS') }}
                  </span>
                  <span
                    class="text-[11px] font-semibold text-n-slate-12 tabular-nums"
                  >
                    {{ items.length }}
                  </span>
                </div>

                <div class="w-px h-3 bg-n-weak" />

                <!-- Eventos do Mês -->
                <div class="flex items-center gap-1.5">
                  <Icon
                    icon="i-lucide-calendar"
                    class="size-3 text-n-blue-10"
                  />
                  <span class="text-[11px] text-n-blue-11">
                    {{ t('PIPELINE.AGENDA.MONTH_EVENTS') }}
                  </span>
                  <span
                    class="text-[11px] font-semibold text-n-blue-11 tabular-nums"
                  >
                    {{ currentMonthEvents.length }}
                  </span>
                </div>

                <div class="w-px h-3 bg-n-weak" />

                <!-- Eventos Remarcados -->
                <div class="flex items-center gap-1.5">
                  <Icon
                    icon="i-lucide-refresh-cw"
                    class="size-3 text-n-slate-10"
                  />
                  <span class="text-[11px] text-n-slate-11">
                    {{ t('PIPELINE.AGENDA.RESCHEDULED_EVENTS') }}
                  </span>
                  <span
                    class="text-[11px] font-semibold text-n-slate-12 tabular-nums"
                  >
                    {{ rescheduledEvents.length }}
                  </span>
                </div>

                <div class="w-px h-3 bg-n-weak" />

                <!-- Eventos Pendentes -->
                <div class="flex items-center gap-1.5">
                  <Icon
                    icon="i-lucide-alert-circle"
                    class="size-3 text-n-ruby-10"
                  />
                  <span class="text-[11px] text-n-ruby-11">
                    {{ t('PIPELINE.AGENDA.NO_DATE_ITEMS') }}
                  </span>
                  <span
                    class="text-[11px] font-semibold text-n-ruby-11 tabular-nums"
                  >
                    {{ pendingEvents.length }}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Seção de Itens Recentes -->
        <div
          class="border-t border-n-weak mt-auto relative z-[3] w-full flex-shrink-0"
        >
          <div class="flex items-center justify-between px-5 py-2">
            <!-- Left: Toggle + Title + Count -->
            <button
              type="button"
              class="flex items-center gap-1.5 rounded-md px-2 py-1 -ml-2 hover:bg-n-alpha-1 transition-colors"
              @click="toggleRecentItems"
            >
              <fluent-icon
                :icon="isRecentItemsExpanded ? 'chevron-down' : 'chevron-right'"
                size="12"
                class="text-n-slate-9 flex-shrink-0"
              />
              <span class="text-[11px] font-semibold text-n-slate-12">
                {{ t('PIPELINE.AGENDA.RECENT_ITEMS') }}
              </span>
              <span
                class="text-[10px] text-n-slate-10 tabular-nums bg-n-alpha-1 px-1.5 py-0.5 rounded"
              >
                {{ filteredRecentItems.length }}/{{ recentItems.length }}
              </span>
            </button>

            <!-- Right: Search + Hint -->
            <div
              v-if="isRecentItemsExpanded"
              class="flex items-center gap-2"
              @click.stop
            >
              <!-- Drag hint -->
              <div
                class="hidden md:flex items-center gap-1.5 text-[10px] text-n-slate-10"
              >
                <fluent-icon icon="drag" size="10" class="flex-shrink-0" />
                <span>{{ t('PIPELINE.AGENDA.DRAG_TO_SCHEDULE') }}</span>
              </div>

              <div class="w-px h-3.5 bg-n-weak hidden md:block" />

              <!-- Expandable Search -->
              <div class="flex items-center">
                <div
                  class="flex items-center overflow-hidden transition-all duration-200 ease-out"
                  :class="showRecentSearch ? 'w-40' : 'w-0'"
                >
                  <div
                    class="h-[26px] w-full rounded-md border border-n-weak bg-n-alpha-1 flex items-center overflow-hidden transition-colors focus-within:border-n-blue-7 focus-within:bg-n-solid-2"
                  >
                    <Icon
                      icon="i-lucide-search"
                      class="size-3 text-n-slate-9 ml-2 flex-shrink-0"
                    />
                    <input
                      id="recent-items-search"
                      v-model="recentItemsSearchQuery"
                      type="search"
                      class="w-full bg-transparent border-none outline-none ring-0 m-0 px-2 text-[11px] text-n-slate-12 placeholder:text-n-slate-9 reset-base no-margin"
                      :placeholder="t('PIPELINE.AGENDA.SEARCH_ITEMS')"
                      @blur="
                        !recentItemsSearchQuery && (showRecentSearch = false)
                      "
                    />
                  </div>
                </div>
                <button
                  type="button"
                  class="flex items-center justify-center w-6 h-6 rounded-md hover:bg-n-alpha-2 text-n-slate-9 hover:text-n-slate-12 transition-colors flex-shrink-0"
                  @click="handleRecentSearch"
                >
                  <Icon icon="i-lucide-search" class="size-5" />
                </button>
              </div>
            </div>
          </div>

          <!-- Cards Container com transição -->
          <transition
            enter-active-class="transition-all duration-300 ease-out"
            leave-active-class="transition-all duration-300 ease-in"
            enter-from-class="opacity-0 max-h-0"
            enter-to-class="opacity-100 max-h-[400px]"
            leave-from-class="opacity-100 max-h-[400px]"
            leave-to-class="opacity-0 max-h-0"
          >
            <div v-show="isRecentItemsExpanded" class="overflow-hidden">
              <!-- Empty state quando não há itens -->
              <div
                v-if="filteredRecentItems.length === 0"
                class="flex flex-col items-center justify-center py-8 text-n-slate-10"
              >
                <Icon
                  icon="i-lucide-search"
                  class="mb-2 size-8 text-n-slate-9"
                />
                <p class="text-sm">
                  {{
                    recentItemsSearchQuery
                      ? t('PIPELINE.AGENDA.NO_SEARCH_RESULTS')
                      : t('PIPELINE.AGENDA.NO_RECENT_ITEMS')
                  }}
                </p>
              </div>
              <div v-else class="overflow-x-auto px-4 pb-3">
                <div class="flex gap-2.5">
                  <div
                    v-for="item in filteredRecentItems"
                    :key="item.id"
                    class="flex-shrink-0 w-72 px-3 py-2 bg-n-solid-2 rounded-lg border border-n-weak transition-all cursor-move hover:border-n-blue-7 hover:shadow-sm"
                    draggable="true"
                    @dragstart="
                      e => {
                        e.dataTransfer.setData('itemId', item.id);
                        e.dataTransfer.effectAllowed = 'move';
                      }
                    "
                  >
                    <div class="flex items-center gap-2">
                      <!-- Priority Bar -->
                      <div
                        class="w-0.5 h-7 rounded-full flex-shrink-0"
                        :class="{
                          'bg-n-ruby-9': item.priority === 'urgent',
                          'bg-n-orange-9': item.priority === 'high',
                          'bg-n-amber-9': item.priority === 'medium',
                          'bg-n-green-9': item.priority === 'low',
                          'bg-n-slate-6':
                            !item.priority || item.priority === 'none',
                        }"
                      />

                      <!-- Conteúdo Principal -->
                      <div class="flex-1 min-w-0">
                        <div class="flex items-center gap-2">
                          <h4
                            class="text-[11px] font-medium text-n-slate-12 truncate flex-1"
                          >
                            {{ item.title }}
                          </h4>
                          <span
                            class="flex-shrink-0 px-1.5 py-px text-[9px] font-medium rounded-md"
                            :style="{
                              backgroundColor: item.stageColor + '15',
                              color: item.stageColor,
                            }"
                          >
                            {{ item.stageName }}
                          </span>
                        </div>
                        <span class="text-[10px] text-n-slate-10">
                          {{ formatShortDate(item.created_at) }}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </transition>
        </div>
      </div>

      <!-- Substituir o Event Details Modal pelo PipelineCardDetails -->
      <Modal
        v-if="selectedItem && showItemDetails"
        v-model:show="showItemDetails"
        size="large"
        @close="handleCloseDetails"
      >
        <PipelineCardDetails
          v-if="selectedItem"
          :item="selectedItem"
          @close="handleCloseDetails"
          @edit="handleItemEdit"
          @item-updated="handleDetailItemUpdated"
        />
      </Modal>

      <!-- Add Event Modal -->
      <Modal v-model:show="showAddModal" @close="() => (showAddModal = false)">
        <div class="flex flex-col max-h-[85vh] overflow-hidden">
          <div class="px-5 pt-5 pb-3">
            <h3 class="text-sm font-semibold text-n-slate-12">
              {{ t('PIPELINE.ADD_ITEM') }}
            </h3>
          </div>
          <PipelineCardForm
            v-if="store.getters['pipelines/getSelectedPipeline']"
            :pipeline-id="store.getters['pipelines/getSelectedPipeline'].id"
            :initial-date="selectedDate"
            @saved="handleItemCreated"
            @close="showAddModal = false"
          />
        </div>
      </Modal>

      <!-- Edit Modal -->
      <Modal
        v-model:show="showEditModal"
        @close="() => (showEditModal = false)"
      >
        <div class="flex flex-col max-h-[85vh] overflow-hidden">
          <div class="px-5 pt-5 pb-3">
            <h3 class="text-sm font-semibold text-n-slate-12">
              {{ t('PIPELINE.EDIT_ITEM') }}
            </h3>
          </div>
          <PipelineCardForm
            v-if="selectedItemToEdit"
            :pipeline-id="store.getters['pipelines/getSelectedPipeline'].id"
            :stage="selectedItemToEdit.pipeline_stage"
            :initial-data="selectedItemToEdit"
            is-editing
            @saved="handleItemUpdated"
            @close="showEditModal = false"
          />
        </div>
      </Modal>

      <!-- Modal de Seleção de Hora -->
      <Modal
        v-model:show="showTimePickerModal"
        size="small"
        @close="cancelSchedule"
      >
        <div class="p-6">
          <h3 class="text-lg font-medium mb-4 text-n-slate-12">
            {{ t('PIPELINE.AGENDA.SELECT_TIME') }}
          </h3>

          <div class="space-y-4">
            <!-- Data selecionada (somente leitura) -->
            <div class="space-y-1.5">
              <label
                class="block text-xs font-semibold text-n-slate-11 uppercase tracking-wider"
              >
                {{ t('PIPELINE.AGENDA.SELECTED_DATE') }}
              </label>
              <div
                class="w-full bg-n-alpha-2 border border-n-weak rounded-lg px-3 py-2.5 text-sm text-n-slate-12"
              >
                {{
                  pendingSchedule?.date
                    ? new Intl.DateTimeFormat(getUserLocale(), {
                        weekday: 'long',
                        day: '2-digit',
                        month: 'long',
                        year: 'numeric',
                      }).format(new Date(pendingSchedule.date))
                    : ''
                }}
              </div>
            </div>

            <!-- Seleção de hora -->
            <div class="space-y-1.5">
              <label
                class="block text-xs font-semibold text-n-slate-11 uppercase tracking-wider"
              >
                {{ t('PIPELINE.AGENDA.SELECT_TIME_LABEL') }}
              </label>
              <input
                v-model="selectedTime"
                type="time"
                class="w-full bg-n-solid-2 border border-n-weak rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-n-blue-6 focus:border-n-blue-7 transition-all text-n-slate-12"
              />
            </div>

            <!-- Opções rápidas de hora -->
            <div class="space-y-1.5">
              <label
                class="block text-xs font-semibold text-n-slate-11 uppercase tracking-wider"
              >
                {{ t('PIPELINE.AGENDA.QUICK_TIMES') }}
              </label>
              <div class="flex flex-wrap gap-2">
                <button
                  v-for="time in [
                    '08:00',
                    '09:00',
                    '10:00',
                    '14:00',
                    '15:00',
                    '16:00',
                    '18:00',
                  ]"
                  :key="time"
                  type="button"
                  class="px-3 py-1.5 text-xs font-medium rounded-lg transition-all border"
                  :class="
                    selectedTime === time
                      ? 'bg-n-blue-9 text-white border-n-blue-10 shadow-sm'
                      : 'bg-n-solid-2 border-n-weak text-n-slate-11 hover:bg-n-alpha-2'
                  "
                  @click="selectedTime = time"
                >
                  {{ time }}
                </button>
              </div>
            </div>
          </div>

          <!-- Botões de ação -->
          <div class="flex justify-end gap-3 mt-6 pt-4 border-t border-n-weak">
            <Button
              type="button"
              variant="ghost"
              color="slate"
              size="sm"
              @click="cancelSchedule"
            >
              {{ t('PIPELINE.CANCEL') }}
            </Button>
            <Button
              type="button"
              variant="solid"
              color="blue"
              size="sm"
              @click="confirmScheduleWithTime"
            >
              {{ t('PIPELINE.AGENDA.CONFIRM_SCHEDULE') }}
            </Button>
          </div>
        </div>
      </Modal>

      <!-- Modal de Agendamento por Clique no Dia -->
      <Modal
        v-model:show="showScheduleModal"
        size="medium"
        @close="cancelScheduleModal"
      >
        <div class="p-6">
          <div class="flex items-center justify-between mb-4">
            <h3 class="text-lg font-medium text-n-slate-12">
              {{ t('PIPELINE.AGENDA.SCHEDULE_ITEM') }}
            </h3>
            <div
              v-if="scheduleModalDate"
              class="px-3 py-1.5 bg-n-blue-3 text-n-blue-11 rounded-lg text-sm font-medium"
            >
              {{
                new Intl.DateTimeFormat(getUserLocale(), {
                  weekday: 'short',
                  day: '2-digit',
                  month: 'short',
                }).format(scheduleModalDate)
              }}
            </div>
          </div>

          <!-- Campo de busca -->
          <div class="relative mb-4">
            <Icon
              icon="i-lucide-search"
              class="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-n-slate-9"
            />
            <input
              v-model="scheduleModalSearchQuery"
              type="text"
              :placeholder="t('PIPELINE.AGENDA.SEARCH_ITEMS_TO_SCHEDULE')"
              class="w-full pl-10 pr-4 py-2.5 text-sm border border-n-weak rounded-lg bg-n-solid-2 text-n-slate-12 placeholder-n-slate-9 focus:ring-2 focus:ring-n-blue-6 focus:border-n-blue-7 transition-all"
            />
            <button
              v-if="scheduleModalSearchQuery"
              type="button"
              class="absolute right-3 top-1/2 -translate-y-1/2 text-n-slate-9 hover:text-n-slate-11"
              @click="scheduleModalSearchQuery = ''"
            >
              <Icon icon="i-lucide-x" class="size-3.5" />
            </button>
          </div>

          <!-- Contagem de itens -->
          <div class="flex items-center justify-between mb-3">
            <span class="text-xs text-n-slate-10">
              {{
                `${t('PIPELINE.AGENDA.UNSCHEDULED_ITEMS')}: ${allUnscheduledItems.length}`
              }}
            </span>
            <span
              v-if="scheduleModalSearchQuery"
              class="text-xs text-n-slate-10"
            >
              {{
                `${t('PIPELINE.AGENDA.SHOWING')}: ${filteredScheduleModalItems.length}`
              }}
            </span>
          </div>

          <!-- Lista de itens -->
          <div class="max-h-[400px] overflow-y-auto space-y-2 mb-4">
            <div
              v-if="filteredScheduleModalItems.length === 0"
              class="flex flex-col items-center justify-center py-12 text-n-slate-10"
            >
              <Icon
                icon="i-lucide-search"
                class="mb-3 size-12 text-n-slate-7"
              />
              <p class="text-sm">
                {{
                  scheduleModalSearchQuery
                    ? t('PIPELINE.AGENDA.NO_SEARCH_RESULTS')
                    : t('PIPELINE.AGENDA.NO_UNSCHEDULED_ITEMS')
                }}
              </p>
            </div>
            <div
              v-for="item in filteredScheduleModalItems"
              :key="item.id"
              class="p-3 rounded-lg border cursor-pointer transition-all"
              :class="{
                'border-n-blue-9 bg-n-blue-3 ring-2 ring-n-blue-6':
                  selectedItemToSchedule?.id === item.id,
                'border-n-weak hover:border-n-slate-7 bg-n-solid-2':
                  selectedItemToSchedule?.id !== item.id,
              }"
              @click="selectItemForSchedule(item)"
            >
              <div class="flex items-start gap-3">
                <!-- Radio indicator -->
                <div
                  class="w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5"
                  :class="{
                    'border-n-blue-9 bg-n-blue-9':
                      selectedItemToSchedule?.id === item.id,
                    'border-n-slate-7': selectedItemToSchedule?.id !== item.id,
                  }"
                >
                  <Icon
                    v-if="selectedItemToSchedule?.id === item.id"
                    icon="i-lucide-check"
                    class="size-3 text-white"
                  />
                </div>

                <!-- Item content -->
                <div class="flex-1 min-w-0">
                  <div class="flex items-center gap-2 mb-1">
                    <h4 class="text-sm font-medium text-n-slate-12 truncate">
                      {{ item.title || t('PIPELINE.UNTITLED_ITEM') }}
                    </h4>
                    <span
                      class="flex-shrink-0 px-2 py-0.5 text-xs rounded-full"
                      :style="{
                        backgroundColor: item.stageColor + '20',
                        color: item.stageColor,
                      }"
                    >
                      {{ item.stageName || t('PIPELINE.NO_STAGE') }}
                    </span>
                  </div>
                  <div class="flex items-center gap-3 text-xs text-n-slate-10">
                    <span class="flex items-center gap-1">
                      <Icon icon="i-lucide-calendar" class="size-3" />
                      {{ formatShortDate(item.created_at) }}
                    </span>
                    <span
                      v-if="item.priority && item.priority !== 'none'"
                      class="flex items-center gap-1"
                      :style="{ color: getPriorityColor(item.priority) }"
                    >
                      <Icon icon="i-lucide-flag" class="size-3" />
                      {{
                        t(`PIPELINE.PRIORITIES.${item.priority.toUpperCase()}`)
                      }}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Botões de ação -->
          <div
            class="flex justify-between items-center pt-4 border-t border-n-weak"
          >
            <Button
              type="button"
              variant="ghost"
              color="slate"
              size="sm"
              @click="
                showAddModal = true;
                showScheduleModal = false;
              "
            >
              <Icon icon="i-lucide-plus" class="size-3.5 mr-1" />
              {{ t('PIPELINE.AGENDA.CREATE_NEW') }}
            </Button>
            <div class="flex gap-3">
              <Button
                type="button"
                variant="ghost"
                color="slate"
                size="sm"
                @click="cancelScheduleModal"
              >
                {{ t('PIPELINE.CANCEL') }}
              </Button>
              <Button
                type="button"
                variant="solid"
                color="blue"
                size="sm"
                :disabled="!selectedItemToSchedule"
                @click="confirmScheduleFromModal"
              >
                {{ t('PIPELINE.AGENDA.SELECT_AND_SCHEDULE') }}
              </Button>
            </div>
          </div>
        </div>
      </Modal>
    </template>
  </div>
</template>
